'use client';

import type { PropsWithChildren } from 'react';
import React, { memo, useMemo } from 'react';
import type { Key } from 'react-aria';
import type { LayoutInfo } from '../layouts/types';
import type { ListFilterState } from '../List/types';
import type { UseAnimateMutationProps } from './useAnimateMutation';
import { useAnimateMutation } from './useAnimateMutation';

const AnimateMutationContext = React.createContext({
  animating: false,
  layoutInfo: new Map<Key, LayoutInfo>(),
  isFullyMeasured: true,
  duration: 250,
});

export const useAnimateMutationContext = () =>
  React.useContext(AnimateMutationContext);

type Props<T> = PropsWithChildren<
  UseAnimateMutationProps & {
    state: ListFilterState<T>;
    isFullyMeasured: boolean;
  }
>;

function AnimateMutationProvider<T>({
  state,
  isFullyMeasured,
  children,
  ...props
}: Props<T>) {
  const { animating } = useAnimateMutation(props, state);

  const context = useMemo(() => {
    return {
      animating,
      layoutInfo: props.layoutInfo,
      isFullyMeasured,
      duration: props.duration,
    };
  }, [animating, props.duration, props.layoutInfo, isFullyMeasured]);

  return (
    <AnimateMutationContext.Provider value={context}>
      {children}
    </AnimateMutationContext.Provider>
  );
}

const _AnimateMutationProvider = memo(AnimateMutationProvider);
_AnimateMutationProvider.displayName = 'AnimationProvider';
export { _AnimateMutationProvider as AnimateMutationProvider };
